import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useApolloClient } from '@apollo/client';
import styled from 'styled-components';
import dayjs from 'dayjs';
import Select from 'react-select';
import { ClipLoader } from 'react-spinners';
import { colors } from 'styles/theme';
import { DAYS_OF_THE_WEEK, FORMAT } from 'constants/date-constants';
import { GET_ORDERS_OF_TOTEM } from 'data/queries';
import { analyticsEvents, analyticsPropertiesConstants, track } from 'tracking/segment';
import { HandleNotification } from 'utils';
import { PopupTitle } from 'styles/pages/settings.styles';
import { TotemButton } from 'ui/Button';
import { getRecurringOrdersForDay } from 'utils/orders';
import {
    OrderFragmentFragment as Order,
    OrderFrequency,
    useCreateNewRecurringOrderMutation,
    useCreatePunctualOrderMutation,
    useGetOrdersOfTotemWithinDateRangeQuery,
} from 'data/__generated__';
import { OrderTypeSelectionProps } from 'pages/Totem/TotemDashboard/TotemDashboard.types';

const FrequencySelect = styled(Select)`
    margin: 1rem 0;
    width: 100%;
`;

type FrequencyOption = { value: string; label: string };
const frequencyOptions: FrequencyOption[] = [
    { value: 'Weekly', label: 'Toutes les semaines' },
    { value: 'BiWeekly', label: 'Toutes les deux semaines' },
    { value: 'Monthly', label: 'Tous les mois' },
    { value: 'Quarterly', label: 'Tous les trois mois' },
    { value: 'BiAnnually', label: 'Tous les six mois' },
];

export interface OrderTypeSelectionState extends OrderTypeSelectionProps {
    setSelectedOrder: (order: Order, frequency: any, handleRecurringOrder: () => void) => void;
}
export const OrderTypeSelection: React.FC<OrderTypeSelectionState> = ({
    ordersOfTotem,
    currentTotem,
    defaultCategoryId,
    selectedDate,
    selectedTimeWindow,
    setSelectedOrder,
}) => {
    // const { _id: totemId } = currentTotem;
    const totemId = currentTotem?._id;
    const apolloClient = useApolloClient();
    const navigate = useNavigate();
    const startDate = selectedDate?.startOf('day').toDate();
    const endDate = selectedDate?.endOf('day').toDate();
    if (!totemId) {
        throw new Error('Totem ID is required to create an order.');
    }
    const {
        loading: ordersLoading,
        error: ordersError,
        data: ordersData,
    } = useGetOrdersOfTotemWithinDateRangeQuery({
        variables: { totemId, startDate, endDate, isPunctual: true },
        skip: !totemId,
        fetchPolicy: 'no-cache', // prevents from creating 2 consecutive orders for the same date
    });
    if (ordersError) {
        throw new Error(ordersError.message);
    }
    const [createNewRecurringOrder, { loading: isNewRecurringCreationLoading }] = useCreateNewRecurringOrderMutation();
    const [createPunctualOrder, { loading: isPunctualCreationLoading }] = useCreatePunctualOrderMutation();

    const [loading, setLoading] = useState(false);
    const weekday: string | undefined = selectedDate
        ? DAYS_OF_THE_WEEK[parseInt(dayjs(selectedDate?._d).format('d'), 10)]?.toLowerCase()
        : undefined;
    const [selectedFrequency, setSelectedFrequency] = useState<FrequencyOption>(frequencyOptions[0]);

    if (isNewRecurringCreationLoading || isPunctualCreationLoading || ordersLoading || loading) {
        return (
            <>
                <PopupTitle>Voulez-vous vous faire livrer automatiquement ?</PopupTitle>
                <LoaderContainer>
                    <ClipLoader color={colors.black} size="30px" />
                </LoaderContainer>
            </>
        );
    }
    const ordersOfTotemWithinDateRange = ordersData?.ordersOfTotemWithinDateRange ?? [];
    const hasSameDayPunctualOrder = ordersOfTotemWithinDateRange.length;

    // const recentOrders = ordersOfTotem?.recentOrders ?? [];

    const { modifiableOrder: sameDayModifiableRecurringOrder } = getRecurringOrdersForDay(
        ordersOfTotem ?? null,
        weekday,
    );

    const createUrl = (orderId: string | number) => `/totem/${totemId}/${defaultCategoryId}/${orderId}`;

    const handleRecurringOrder = async () => {
        try {
            setLoading(true);
            const data = await createNewRecurringOrder({
                variables: {
                    totemId,
                    deliveryDate: dayjs(selectedDate._d).format(FORMAT),
                    deliveryTimeWindow: selectedTimeWindow,
                    frequency: selectedFrequency.value as OrderFrequency,
                },
            });
            const newRecurringOrder = data.data?.createNewRecurringOrder;
            const recentOrders = ordersOfTotem?.recentOrders ?? [];
            apolloClient.writeQuery({
                query: GET_ORDERS_OF_TOTEM,
                variables: { totemId },
                data: {
                    ordersOfTotem: {
                        ...ordersOfTotem,
                        recentOrders: [
                            ...recentOrders,
                            {
                                ...newRecurringOrder,
                                productsWithQuantityPicked: [],
                                productsRequestedButNotFullyDelivered: [],
                            },
                        ],
                    },
                },
            });
            navigate(createUrl(newRecurringOrder?._id ?? ''));
        } catch (error) {
            HandleNotification(error);
            setLoading(false);
        }
    };

    const handlePunctualOrder = async () => {
        try {
            setLoading(true);
            const data = await createPunctualOrder({
                variables: {
                    totemId,
                    deliveryDate: dayjs(selectedDate._d).format(FORMAT),
                    deliveryTimeWindow: selectedTimeWindow,
                },
            });
            const newPunctualOrder = data.data?.createPunctualOrder;
            const recentOrders = ordersOfTotem?.recentOrders ?? [];
            apolloClient.writeQuery({
                query: GET_ORDERS_OF_TOTEM,
                variables: { totemId },
                data: {
                    ordersOfTotem: {
                        ...ordersOfTotem,
                        recentOrders: [
                            ...recentOrders,
                            {
                                ...newPunctualOrder,
                                productsWithQuantityPicked: [],
                                productsRequestedButNotFullyDelivered: [],
                            },
                        ],
                    },
                },
            });
            navigate(createUrl(newPunctualOrder?._id ?? ''));
        } catch (error) {
            HandleNotification(error);
            setLoading(false);
        }
    };

    return (
        <>
            <PopupTitle>Voulez-vous vous faire livrer automatiquement ?</PopupTitle>
            <OrderTypeContainer>
                <FrequencySelect
                    value={selectedFrequency}
                    options={frequencyOptions}
                    onChange={(newValue: unknown) => {
                        if (newValue) setSelectedFrequency(newValue as FrequencyOption);
                    }}
                    placeholder="Sélectionner une fréquence"
                    isDisabled={loading}
                />
                <TotemButton
                    $margins={[1, 1, 0.5, 1]}
                    data-test="create-recurring-order-button"
                    onClick={async () => {
                        if (sameDayModifiableRecurringOrder) {
                            setSelectedOrder(sameDayModifiableRecurringOrder, selectedFrequency, handleRecurringOrder);
                        } else {
                            handleRecurringOrder();
                            track(analyticsEvents.CREATE_ORDER, {
                                [analyticsPropertiesConstants.ORDER.IS_PUNCTUAL]: false,
                                [analyticsPropertiesConstants.ORDER.FREQUENCY]: selectedFrequency.value,
                            });
                        }
                    }}
                >
                    Oui
                </TotemButton>
                <Tip>
                    Votre commande sera automatiquement renouvelée et livrée selon la fréquence choisie (elle reste
                    modifiable)
                </Tip>
                <TotemButton
                    $type="secondary"
                    $margins={[1, 1, 0.5, 1]}
                    onClick={async () => {
                        handlePunctualOrder();
                        track(analyticsEvents.CREATE_ORDER, {
                            [analyticsPropertiesConstants.ORDER.IS_PUNCTUAL]: true,
                        });
                    }}
                    data-test="create-punctual-order-button"
                >
                    Non
                </TotemButton>
                <Tip>Votre commande ne sera pas automatiquement renouvelée</Tip>
                {!!hasSameDayPunctualOrder && (
                    <Tip color="red">Attention : vous avez déjà une commande ponctuelle pour ce jour.</Tip>
                )}
            </OrderTypeContainer>
        </>
    );
};

const OrderTypeContainer = styled.div`
    display: flex;
    flex-direction: column;
    justify-content: center;
`;

const LoaderContainer = styled.div`
    display: flex;
    flex: 1;
    align-items: center;
    justify-content: center;
`;

interface TipProps {
    color?: string;
}
const Tip = styled.div<TipProps>`
    color: ${(props) => (props.color === 'red' ? props.theme.colors.red : props.theme.colors.pantinGrey)};
    font-size: 14px;
    text-align: center;
    margin-bottom: 20px;
    max-width: 470px;
`;
